import type {
    AppModule,
    AppSubmodule,
    ExposedMenu,
    MenuGroup,
    SideLink,
} from '@moniebook-web/components/widgets/sidebar';
import { ArrowUpDownLine } from '@moniebook-web/icons/arrows/arrow-up-down-line';
import { BankLine } from '@moniebook-web/icons/buildings/bank-line';
import { CalendarLine } from '@moniebook-web/icons/business/calendar-line';
import { PieChart2Line } from '@moniebook-web/icons/business/pie-chart-2-line';
import { RestartLine } from '@moniebook-web/icons/device/restart-line';
import { BillLine } from '@moniebook-web/icons/document/bill-line';
import { BookletLine } from '@moniebook-web/icons/document/booklet-line';
import { Folder2Line } from '@moniebook-web/icons/document/folder-2-line';
import { PriceTag2Line } from '@moniebook-web/icons/finance/price-tag-2-line';
import { ShoppingBasket2Line } from '@moniebook-web/icons/finance/shopping-basket-2-line';
import { ShoppingBasketLine } from '@moniebook-web/icons/finance/shopping-basket-line';
import { Dice4Line } from '@moniebook-web/icons/game-sports/dice-4-line';
import { Inventory } from '@moniebook-web/icons/sidebar-rail/inventory';
import { StockManagement } from '@moniebook-web/icons/sidebar-rail/stock-management';
import { Apps2AddLine } from '@moniebook-web/icons/system/apps-2-add-line';
import { UserSharedLine } from '@moniebook-web/icons/user-faces/user-shared-line';
import {
    ApplicationModule,
    MENU_PARENTS,
    MENU_SECTIONS,
    SETTINGS_SECTIONS,
} from '@moniebook-web/types';

const PRODUCTS_SIDEBAR_COLOR = '#6EA31B';
const ENABLE_ITEM_MODIFIERS = 'moniebook.pos.enableItemModifiers';
const ENABLE_PRICE_REVIEW_AND_BILL_FLAG =
    'moniebook.pos.enablePriceReviewAndBill';
const ENABLE_PROCUREMENT_SETTINGS = 'moniebook.pos.enableProcurementSettings';

const ITEMS_PERMISSIONS = [
    'MANAGE_ITEM',
    'VIEW_ITEM',
    'CREATE_ITEM',
    'EDIT_ITEM',
    'DELETE_ITEM',
    'IMPORT_ITEM',
    'EXPORT_ITEM',
];
const STOCK_ADJUSTMENT_PERMISSIONS = [
    'VIEW_STOCK_ADJUSTMENT',
    'MANAGE_STOCK_ADJUSTMENT',
];
const STOCK_HISTORY_PERMISSIONS = ['VIEW_STOCK_HISTORY'];
const STOCK_TRANSFER_PERMISSIONS = [
    'CANCEL_STOCK_TRANSFER',
    'VIEW_STOCK_TRANSFER',
    'MANAGE_STOCK_TRANSFER',
    'CREATE_STOCK_TRANSFER',
    'RECEIVE_STOCK_TRANSFER',
];
const PRODUCTION_AND_RECIPES_PERMISSIONS = [
    'VIEW_PRODUCTIONS',
    'CREATE_PRODUCTIONS',
    'CAN_REVERT_PRODUCTIONS',
    'VIEW_RECIPES',
    'CREATE_RECIPES',
    'DELETE_RECIPE',
];
const PURCHASE_ORDERS_PERMISSIONS = [
    'VIEW_PURCHASE_ORDERS',
    'CREATE_PURCHASE_ORDERS',
    'APPROVE_PURCHASE_ORDERS',
    'DELETE_PURCHASE_ORDERS',
    'DRAFT_PURCHASE_ORDERS',
];
const SUPPLIERS_PERMISSIONS = [
    'VIEW_SUPPLIER',
    'EDIT_SUPPLIER',
    'DELETE_SUPPLIER',
    'MANAGE_SUPPLIER',
];
const PRICING_GROUPS_PERMISSIONS = [
    'VIEW_PRICING_GROUP',
    'CREATE_PRICING_GROUP',
    'UPDATE_PRICING_GROUP',
    'DELETE_PRICING_GROUP',
];
const MEASUREMENT_UNITS_PERMISSIONS = [
    'CREATE_MEASUREMENT_UNIT',
    'VIEW_MEASUREMENT_UNIT',
    'EDIT_MEASUREMENT_UNIT',
    'DELETE_MEASUREMENT_UNIT',
    'MANAGE_MEASUREMENT_UNIT',
];
const REPORTS_PERMISSIONS = ['VIEW_REPORT'];
const STOCK_RECONCILIATION_PERMISSIONS = [
    'VIEW_STOCK_RECONCILIATION',
    'CREATE_STOCK_RECONCILIATION',
    'APPROVE_STOCK_RECONCILIATION',
];

const GRN_PERMISSIONS = ['VIEW_GRN', 'CREATE_GRN', 'APPROVE_GRN'];

const BILL_PERMISSIONS = [
    'VIEW_BILL',
    'MANAGE_BILL',
    'CREATE_BILL',
    'APPROVE_BILL',
];

const PROCUREMENT_SETTINGS_PERMISSIONS = ['APPROVE_PURCHASE_ORDERS'];

const PRODUCTS_REPORTS_BASE_FILTER_KEYS = [
    'dateFrom',
    'dateTo',
    'timeFrom',
    'timeTo',
    'staff',
    'branches',
    'chartType',
    'groupBy',
];

export const productsMenu: ExposedMenu = {
    menuGroups: [
        {
            id: MENU_PARENTS.STOCK_MANAGEMENT,
            name: 'Stock management',
            order: 4,
            icon: <Folder2Line />,
            sectionId: MENU_SECTIONS.INVENTORY,
            color: PRODUCTS_SIDEBAR_COLOR,
        },
        {
            id: MENU_PARENTS.PROCUREMENT,
            name: 'Procurement',
            order: 5,
            icon: <BookletLine />,
            sectionId: MENU_SECTIONS.INVENTORY,
            color: PRODUCTS_SIDEBAR_COLOR,
        },
    ] as MenuGroup[],
    submenu: [
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            order: 1,
            accessCodeList: ITEMS_PERMISSIONS,
            name: 'All items',
            to: '/dashboard/items',
            icon: <ShoppingBasketLine />,
            grantAccessIfAny: true,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            order: 2,
            accessCodeList: ITEMS_PERMISSIONS,
            name: 'Categories',
            to: '/dashboard/categories',
            icon: <Dice4Line />,
            grantAccessIfAny: true,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            order: 3,
            accessCodeList: ITEMS_PERMISSIONS,
            name: 'Modifiers',
            to: '/dashboard/modifiers',
            icon: <Apps2AddLine />,
            featureFlag: ENABLE_ITEM_MODIFIERS,
            grantAccessIfAny: true,
        },
        {
            sectionId: SETTINGS_SECTIONS.INVENTORY,
            order: 1,
            accessCodeList: MEASUREMENT_UNITS_PERMISSIONS,
            name: 'Measurement units',
            to: '/dashboard/settings/measurement-units',
            grantAccessIfAny: true,
            showInSettings: true,
        },
        {
            sectionId: SETTINGS_SECTIONS.INVENTORY,
            order: 2,
            accessCodeList: PRICING_GROUPS_PERMISSIONS,
            name: 'Pricing groups',
            to: '/dashboard/settings/pricing-groups',
            grantAccessIfAny: true,
            showInSettings: true,
        },
        {
            sectionId: SETTINGS_SECTIONS.INVENTORY,
            order: 3,
            accessCodeList: PROCUREMENT_SETTINGS_PERMISSIONS,
            name: 'Procurement settings',
            to: '/dashboard/settings/procurement',
            featureFlag: ENABLE_PROCUREMENT_SETTINGS,
            grantAccessIfAny: true,
            showInSettings: true,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 1,
            accessCodeList: STOCK_ADJUSTMENT_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/stock-overview',
            name: 'Stock overview',
            icon: <ArrowUpDownLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 2,
            accessCodeList: STOCK_ADJUSTMENT_PERMISSIONS,
            grantAccessIfAny: true,
            name: 'Stock adjustment',
            to: '/dashboard/stock-adjustment',
            icon: <BankLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 3,
            accessCodeList: STOCK_HISTORY_PERMISSIONS,
            name: 'Stock history',
            to: '/dashboard/stock-history',
            grantAccessIfAny: true,
            icon: <RestartLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 4,
            accessCodeList: STOCK_TRANSFER_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/stock-transfer',
            name: 'Stock transfer',
            icon: <ArrowUpDownLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 5,
            accessCodeList: [
                ...STOCK_ADJUSTMENT_PERMISSIONS,
                ...ITEMS_PERMISSIONS,
            ],
            grantAccessIfAny: true,
            to: '/dashboard/batch-management',
            name: 'Batch management',
            icon: <BankLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 6,
            accessCodeList: PRODUCTION_AND_RECIPES_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/production-recipes',
            name: 'Production & Recipes',
            icon: <RestartLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.STOCK_MANAGEMENT,
            order: 7,
            accessCodeList: STOCK_RECONCILIATION_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/stock-reconciliation',
            name: 'Stock reconciliation',
            icon: <ArrowUpDownLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.PROCUREMENT,
            order: 1,
            accessCodeList: PURCHASE_ORDERS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/purchase-orders',
            name: 'Purchasing orders',
            icon: <ShoppingBasket2Line />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.PROCUREMENT,
            order: 2,
            accessCodeList: GRN_PERMISSIONS,
            featureFlag: ENABLE_PRICE_REVIEW_AND_BILL_FLAG,
            grantAccessIfAny: true,
            to: '/dashboard/goods-received',
            name: 'Goods received',
            icon: <PriceTag2Line />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.PROCUREMENT,
            order: 3,
            accessCodeList: BILL_PERMISSIONS,
            featureFlag: ENABLE_PRICE_REVIEW_AND_BILL_FLAG,
            grantAccessIfAny: true,
            to: '/dashboard/purchase-order-bills',
            name: 'Bills',
            icon: <BillLine />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.PROCUREMENT,
            order: 4,
            accessCodeList: ['MANAGE_ITEM'],
            featureFlag: ENABLE_PRICE_REVIEW_AND_BILL_FLAG,
            grantAccessIfAny: true,
            to: '/dashboard/pricing-reviews',
            name: 'Pricing reviews',
            icon: <PriceTag2Line />,
        },
        {
            sectionId: MENU_SECTIONS.INVENTORY,
            parentId: MENU_PARENTS.PROCUREMENT,
            order: 5,
            accessCodeList: SUPPLIERS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/suppliers',
            name: 'Vendors',
            icon: <UserSharedLine />,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 9,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            name: 'Expiring inventory',
            to: '/dashboard/reports/expiring-inventory',
            icon: <CalendarLine />,
            keepSearchParams: true,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 10,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/reports/inventory-valuation',
            name: 'Inventory valuation',
            icon: <PieChart2Line />,
            keepSearchParams: true,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 11,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/reports/reconciliation-report',
            name: 'Reconciliation report',
            icon: <PieChart2Line />,
            keepSearchParams: true,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.REPORTS,
            order: 12,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/reports/stock-movements',
            name: 'Stock movement',
            keepSearchParams: true,
            keepSearchList: PRODUCTS_REPORTS_BASE_FILTER_KEYS,
            icon: <ArrowUpDownLine />,
        },
    ] as SideLink[],
    modules: [
        {
            id: ApplicationModule.INVENTORY,
            name: 'Inventory',
            order: 6,
            icon: <Inventory />,
            backgroundColor: '#F5D0FE',
            foregroundColor: '#E979F9',
        },
        {
            id: ApplicationModule.STOCK_MANAGEMENT,
            name: 'Stock management',
            order: 7,
            icon: <StockManagement />,
            backgroundColor: '#F5D0FE',
            foregroundColor: '#E979F9',
        },
    ] as AppModule[],
    subModules: [
        {
            parentId: ApplicationModule.INVENTORY,
            order: 1,
            accessCodeList: ITEMS_PERMISSIONS,
            name: 'All items',
            to: '/dashboard/items',
            icon: <ShoppingBasketLine />,
            grantAccessIfAny: true,
        },
        {
            parentId: ApplicationModule.INVENTORY,
            order: 2,
            accessCodeList: ITEMS_PERMISSIONS,
            name: 'Categories',
            to: '/dashboard/categories',
            icon: <Dice4Line />,
            grantAccessIfAny: true,
        },
        {
            parentId: ApplicationModule.INVENTORY,
            order: 3,
            accessCodeList: ITEMS_PERMISSIONS,
            name: 'Modifiers',
            to: '/dashboard/modifiers',
            icon: <Apps2AddLine />,
            featureFlag: ENABLE_ITEM_MODIFIERS,
            grantAccessIfAny: true,
        },
        {
            parentId: ApplicationModule.INVENTORY,
            order: 4,
            accessCodeList: SUPPLIERS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/suppliers',
            name: 'Vendors',
            icon: <UserSharedLine />,
        },
        {
            sectionId: SETTINGS_SECTIONS.INVENTORY,
            order: 1,
            accessCodeList: MEASUREMENT_UNITS_PERMISSIONS,
            name: 'Measurement units',
            to: '/dashboard/settings/measurement-units',
            grantAccessIfAny: true,
            showInSettings: true,
        },
        {
            sectionId: SETTINGS_SECTIONS.INVENTORY,
            order: 2,
            accessCodeList: PRICING_GROUPS_PERMISSIONS,
            name: 'Pricing groups',
            to: '/dashboard/settings/pricing-groups',
            grantAccessIfAny: true,
            showInSettings: true,
        },
        {
            sectionId: SETTINGS_SECTIONS.INVENTORY,
            order: 3,
            accessCodeList: PROCUREMENT_SETTINGS_PERMISSIONS,
            name: 'Procurement settings',
            to: '/dashboard/settings/procurement',
            featureFlag: ENABLE_PROCUREMENT_SETTINGS,
            grantAccessIfAny: true,
            showInSettings: true,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 1,
            accessCodeList: STOCK_ADJUSTMENT_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/stock-overview',
            name: 'Stock overview',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 2,
            accessCodeList: STOCK_ADJUSTMENT_PERMISSIONS,
            grantAccessIfAny: true,
            name: 'Stock adjustment',
            to: '/dashboard/stock-adjustment',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 3,
            accessCodeList: STOCK_HISTORY_PERMISSIONS,
            name: 'Stock history',
            to: '/dashboard/stock-history',
            grantAccessIfAny: true,
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 4,
            accessCodeList: STOCK_TRANSFER_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/stock-transfer',
            name: 'Stock transfer',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 5,
            accessCodeList: [
                ...STOCK_ADJUSTMENT_PERMISSIONS,
                ...ITEMS_PERMISSIONS,
            ],
            grantAccessIfAny: true,
            to: '/dashboard/batch-management',
            name: 'Batch management',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 6,
            accessCodeList: PRODUCTION_AND_RECIPES_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/production-recipes',
            name: 'Production & Recipes',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 7,
            accessCodeList: PURCHASE_ORDERS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/purchase-orders',
            name: 'Purchasing orders',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 8,
            accessCodeList: GRN_PERMISSIONS,
            featureFlag: ENABLE_PRICE_REVIEW_AND_BILL_FLAG,
            grantAccessIfAny: true,
            to: '/dashboard/goods-received',
            name: 'Goods received',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 9,
            accessCodeList: BILL_PERMISSIONS,
            featureFlag: ENABLE_PRICE_REVIEW_AND_BILL_FLAG,
            grantAccessIfAny: true,
            to: '/dashboard/purchase-order-bills',
            name: 'Bills',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 10,
            accessCodeList: ['MANAGE_ITEM'],
            featureFlag: ENABLE_PRICE_REVIEW_AND_BILL_FLAG,
            grantAccessIfAny: true,
            to: '/dashboard/pricing-reviews',
            name: 'Pricing reviews',
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.STOCK_MANAGEMENT,
            order: 11,
            accessCodeList: STOCK_RECONCILIATION_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/stock-reconciliation',
            name: 'Stock reconciliation',
            icon: <BankLine />,
        },

        {
            parentId: ApplicationModule.REPORTS,
            order: 9,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            name: 'Expiring inventory',
            to: '/dashboard/reports/expiring-inventory',
            icon: <CalendarLine />,
            keepSearchParams: true,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 10,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/reports/inventory-valuation',
            name: 'Inventory valuation',
            icon: <PieChart2Line />,
            keepSearchParams: true,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 11,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/reports/reconciliation-report',
            name: 'Reconciliation report',
            icon: <RestartLine />,
            keepSearchParams: true,
        },
        {
            parentId: ApplicationModule.REPORTS,
            order: 12,
            accessCodeList: REPORTS_PERMISSIONS,
            grantAccessIfAny: true,
            to: '/dashboard/reports/stock-movements',
            name: 'Stock movement',
            keepSearchParams: true,
            keepSearchList: PRODUCTS_REPORTS_BASE_FILTER_KEYS,
            icon: <ArrowUpDownLine />,
        },
    ] as AppSubmodule[],
};
